import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  articleData?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
  jsonLd?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  articleData,
  jsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    const fullTitle = title.includes('文爱社区') ? title : `${title} - 文爱社区`;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const pageUrl = canonicalUrl || window.location.href;
    const canonEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonEl) canonEl.setAttribute('href', pageUrl);

    setMeta('og:title', ogTitle || title, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', pageUrl, true);

    if (articleData) {
      if (articleData.publishedTime) setMeta('article:published_time', articleData.publishedTime, true);
      if (articleData.modifiedTime) setMeta('article:modified_time', articleData.modifiedTime, true);
      if (articleData.author) setMeta('article:author', articleData.author, true);
      if (articleData.tags && articleData.tags.length > 0) {
        articleData.tags.forEach((tag) => {
          const tagEl = document.createElement('meta');
          tagEl.setAttribute('property', 'article:tag');
          tagEl.setAttribute('content', tag);
          document.head.appendChild(tagEl);
        });
      }
    }

    // Remove previous JSON-LD
    const prevScripts = document.querySelectorAll('script[data-dynamic-ld]');
    prevScripts.forEach((s) => s.remove());

    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-dynamic-ld', 'true');
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      const dynScripts = document.querySelectorAll('script[data-dynamic-ld]');
      dynScripts.forEach((s) => s.remove());
      const tagEls = document.querySelectorAll('meta[property="article:tag"]');
      tagEls.forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, articleData, jsonLd]);

  return null;
}